import Text from '@root/components/v2/ui-elements/Text';
import { STATUS_COLORS, UI_COLORS } from '@root/utilities/pastelColors';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import YesNo from '@root/components/YesNo';

const formatDuration = seconds => {
  if (seconds == null) return '-';
  const totalSeconds = Math.round(seconds);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  if (totalSeconds < 3600) {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return secs ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatNumberToTwoDigits = value => {
  if (value == null) return '-';
  return Number(value).toLocaleString();
};

const ScanCoverageCell = ({ scanned, total }) => {
  const progress = total ? Math.min((scanned / total) * 100, 100) : 0;
  const progressColor =
    progress === 100 ? STATUS_COLORS.success : progress >= 80 ? STATUS_COLORS.pending : STATUS_COLORS.error;

  return (
    <div className="min-w-[180px]">
      <Text className="mb-2 text-[13px]" font="semi-bold" style={{ color: UI_COLORS.text.primary }}>
        {scanned}/{total} scanned
      </Text>
      <div
        className="mb-2 h-[7px] w-full overflow-hidden rounded-full"
        style={{ backgroundColor: UI_COLORS.background.tertiary }}>
        <div className="h-full rounded-full" style={{ width: `${progress}%`, backgroundColor: progressColor }} />
      </div>
    </div>
  );
};

const StatusBadge = ({ status, label }) => {
  const statusStyles =
    status === 'clean'
      ? {
          borderColor: `${STATUS_COLORS.success}55`,
          backgroundColor: `${STATUS_COLORS.success}1A`,
          color: STATUS_COLORS.success,
        }
      : {
          borderColor: `${STATUS_COLORS.error}55`,
          backgroundColor: `${STATUS_COLORS.error}1A`,
          color: STATUS_COLORS.error,
        };

  return (
    <Text className="rounded-full border px-3 py-1 text-xs font-bold" font="bold" element="span" style={statusStyles}>
      {label}
    </Text>
  );
};

const PillBadge = ({
  tone = 'success',
  label,
  // withDot = false
}) => {
  const color =
    tone === 'success' ? STATUS_COLORS.success : tone === 'warning' ? STATUS_COLORS.pending : STATUS_COLORS.error;
  return (
    <Text
      className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs"
      font="bold"
      element="span"
      style={{ borderColor: `${color}55`, backgroundColor: `${color}1A`, color }}>
      {/* {withDot && (
        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} />
      )} */}
      {label}
    </Text>
  );
};

export const clamavFleetColumns = [
  {
    field: 'pod_name',
    headerName: 'Engine Pod',
    minWidth: 220,
    sortable: false,
    renderCell: params => (
      <div className="flex flex-col">
        <Text className="text-[13px]" font="bold" style={{ color: UI_COLORS.text.primary }}>
          {params.row.pod_name ?? '-'}
        </Text>
        <Text className="text-[12px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
          {params.row.pod_ip ?? '-'}
        </Text>
      </div>
    ),
  },
  {
    field: 'node_name',
    headerName: 'Node',
    minWidth: 200,
    sortable: false,
    renderCell: params => (
      <Text className="font-mono text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {params.row.node_name?.split('.')[0] ?? '-'}
      </Text>
    ),
  },
  {
    field: 'engine_version',
    headerName: 'Engine',
    minWidth: 200,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {params.row.engine_version ?? '-'}
      </Text>
    ),
  },
  {
    field: 'db_version',
    headerName: 'Signature DB',
    minWidth: 200,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {params.row.db_version ?? '-'}
      </Text>
    ),
  },
  {
    field: 'db_date',
    headerName: 'DB Date',
    minWidth: 200,
    sortable: false,
    renderCell: params => DateFormatPipe(params.row.db_date),
  },
  {
    field: 'stale',
    headerName: 'Freshness',
    minWidth: 200,
    sortable: false,
    renderCell: params =>
      params.row.stale ? <PillBadge tone="warning" label="Stale" /> : <PillBadge tone="success" label="Current" />,
  },
  {
    field: 'reachable',
    headerName: 'Reachable',
    minWidth: 200,
    sortable: false,
    renderCell: params => <YesNo yes={params.row.reachable} />,
  },
];

export const malwareColumns = [
  {
    field: 'cluster_name',
    headerName: 'Cluster',
    minWidth: 240,
    sort: true,
    renderCell: params => (
      <Text className="text-[13px]" font="semi-bold" style={{ color: UI_COLORS.text.primary }}>
        {params.row.cluster_name ?? '-'}
      </Text>
    ),
  },
  {
    field: 'namespace',
    headerName: 'Namespace',
    minWidth: 180,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {params.row.namespace ?? '-'}
      </Text>
    ),
  },
  {
    field: 'total_scan_errors',
    headerName: 'Status',
    minWidth: 140,
    sort: true,
    renderCell: params => {
      const errorCount = params.row.total_scan_errors ?? 0;
      const status = errorCount > 0 ? 'errors' : 'clean';
      const label = errorCount > 0 ? `${errorCount} ${errorCount === 1 ? 'Error' : 'Errors'}` : 'Clean';
      return <StatusBadge status={status} label={label} />;
    },
  },
  {
    field: 'total_containers_scanned',
    headerName: 'Container Coverage',
    minWidth: 220,
    sort: true,
    renderCell: params => {
      const scanned = params.row.total_containers_scanned ?? 0;
      const total = scanned + (params.row.total_skipped_containers ?? 0);
      return <ScanCoverageCell scanned={scanned} total={total} />;
    },
  },
  {
    field: 'total_files_scanned',
    headerName: 'Files Scanned',
    minWidth: 160,
    sort: true,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {formatNumberToTwoDigits(params.row.total_files_scanned)}
      </Text>
    ),
  },
  {
    field: 'total_infected_files',
    headerName: 'Infected',
    minWidth: 120,
    sort: true,
    renderCell: params => {
      const infected = params.row.total_infected_files ?? 0;
      return (
        <Text
          className="text-[13px]"
          font="semi-bold"
          style={{ color: infected > 0 ? STATUS_COLORS.error : UI_COLORS.text.secondary }}>
          {infected}
        </Text>
      );
    },
  },
  {
    field: 'total_scan_time_seconds',
    headerName: 'Duration',
    minWidth: 140,
    sort: true,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {formatDuration(params.row.total_scan_time_seconds)}
      </Text>
    ),
  },
  {
    field: 'scan_timestamp',
    headerName: 'Scan Time',
    minWidth: 200,
    sort: true,
    renderCell: params => DateFormatPipe(params.row.scan_timestamp),
  },
];

export const vulnerabilityColumns = [
  {
    field: 'cluster_name',
    headerName: 'Cluster',
    minWidth: 240,
    sort: true,
    renderCell: params => (
      <Text className="text-[13px]" font="semi-bold" style={{ color: UI_COLORS.text.primary }}>
        {params.row.cluster_name ?? '-'}
      </Text>
    ),
  },
  {
    field: 'namespace',
    headerName: 'Namespace',
    minWidth: 180,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {params.row.namespace ?? '-'}
      </Text>
    ),
  },
  {
    field: 'total_scan_errors',
    headerName: 'Status',
    minWidth: 140,
    sort: true,
    renderCell: params => {
      const errorCount = params.row.total_scan_errors ?? 0;
      const status = errorCount > 0 ? 'errors' : 'clean';
      const label = errorCount > 0 ? `${errorCount} ${errorCount === 1 ? 'Error' : 'Errors'}` : 'Clean';
      return <StatusBadge status={status} label={label} />;
    },
  },
  {
    field: 'total_containers_scanned',
    headerName: 'Container Coverage',
    minWidth: 220,
    sort: true,
    renderCell: params => {
      const scanned = params.row.total_containers_scanned ?? 0;
      const total = scanned + (params.row.total_skipped_containers ?? 0);
      return <ScanCoverageCell scanned={scanned} total={total} />;
    },
  },
  {
    field: 'total_files_scanned',
    headerName: 'Files Scanned',
    minWidth: 160,
    sort: true,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {formatNumberToTwoDigits(params.row.total_files_scanned)}
      </Text>
    ),
  },
  {
    field: 'total_infected_files',
    headerName: 'Infected',
    minWidth: 120,
    sort: true,
    renderCell: params => {
      const infected = params.row.total_infected_files ?? 0;
      return (
        <Text
          className="text-[13px]"
          font="semi-bold"
          style={{ color: infected > 0 ? STATUS_COLORS.error : UI_COLORS.text.secondary }}>
          {infected}
        </Text>
      );
    },
  },
  {
    field: 'total_scan_time_seconds',
    headerName: 'Duration',
    minWidth: 140,
    sort: true,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {formatDuration(params.row.total_scan_time_seconds)}
      </Text>
    ),
  },
  {
    field: 'scan_timestamp',
    headerName: 'Scan Time',
    minWidth: 200,
    sort: true,
    renderCell: params => DateFormatPipe(params.row.scan_timestamp),
  },
];
